import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'antd';
import { DefaultPageLayout } from 'layouts/default-page-layout';
import { useGetFutureExpensesStatistics } from 'api/statistic/use-get-future-expenses-statistics';
import { useGetFuturePaymentsStatistics } from 'api/statistic/use-get-future-payments-statistics';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard';
import { LinearChart } from 'components/insights/linear-chart';
import { FutureExpensePointModal } from 'components/statistics/future-expense-point-modal';
import { FutureExpensesTotals } from 'components/statistics/future-expenses-totals';
import { FuturePaymentsTotals } from 'components/statistics/future-payments-totals';

const FuturePaymentsPageComponent = () => {
  const { t } = useTranslation();
  const { data: paymentsData, isFetching: isPaymentsFetching } = useGetFuturePaymentsStatistics();
  const { data: expensesData, isFetching: isExpensesFetching } = useGetFutureExpensesStatistics();

  const [selectedExpensePoint, setSelectedExpensePoint] = useState<{ x: number } | null>(null);

  const handleExpensePointClick = (point: { x: number }) => {
    setSelectedExpensePoint(point);
  };

  const futurePaymentsSeries = [
    { name: t('future-payments:future_payments'), data: paymentsData?.series.futurePayments ?? [], unit: '€' },
    {
      name: t('future-payments:future_expenses'),
      data: expensesData?.series.futureExpenses ?? [],
      unit: '€',
      onPointClick: handleExpensePointClick,
    },
  ];

  const debtReductionSeries = [
    { name: t('future-payments:debt_reduction'), data: paymentsData?.series.debtReduction ?? [], unit: '€' },
    {
      name: t('future-payments:expenses_reduction'),
      data: expensesData?.series.expensesReduction ?? [],
      unit: '€',
    },
  ];

  return (
    <DefaultPageLayout>
      <Skeleton loading={isPaymentsFetching || isExpensesFetching}>
        {paymentsData && expensesData && (
          <>
            <FuturePaymentsTotals totals={paymentsData.totals} />
            <FutureExpensesTotals totals={expensesData.totals} />
            <LinearChart title={t('future-payments:future_payments')} mainSeries={futurePaymentsSeries} />
            <LinearChart title={t('future-payments:debt_reduction')} mainSeries={debtReductionSeries} />
          </>
        )}
      </Skeleton>
      {selectedExpensePoint && (
        <FutureExpensePointModal
          isOpen={!!selectedExpensePoint}
          point={selectedExpensePoint}
          onClose={() => setSelectedExpensePoint(null)}
        />
      )}
    </DefaultPageLayout>
  );
};

export const FuturePaymentsPage = withRoleGuard(FuturePaymentsPageComponent, [UserRole.admin]);
